import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reliably scrolls to a hash target (e.g. /portal/co-marketing#add-our-logo)
 * after client-side navigation. The browser only auto-scrolls to a hash on a
 * full document load, not on React Router navigations, and the target section
 * may still be mounting (async data). So we poll for the element for a short
 * window and scroll it into view once it exists.
 *
 * Auth-callback hashes (#access_token=..., recovery/invite links, errors) are
 * ignored here - those are handled by AuthHashRedirector.
 */
export function ScrollToHashOnNavigation() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const isAuthHash =
      hash.includes("access_token=") ||
      hash.includes("error=") ||
      hash.includes("error_code=") ||
      /type=(magiclink|recovery|signup|invite)/.test(hash);
    if (isAuthHash) return;

    const id = decodeURIComponent(hash.slice(1));
    if (!id) return;

    let cancelled = false;
    let rafId = 0;
    const start = Date.now();
    const TIMEOUT_MS = 3000;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (Date.now() - start < TIMEOUT_MS) {
        rafId = window.requestAnimationFrame(tryScroll);
      }
    };

    // Defer one frame so the destination route has a chance to render.
    rafId = window.requestAnimationFrame(tryScroll);

    return () => {
      cancelled = true;
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [pathname, hash]);

  return null;
}
